// ============================================================================
// KeyboardShortcutsModal.jsx — Press "?" anywhere to show shortcuts
// Mounted globally in App.jsx
// ============================================================================

import { useState, useEffect } from 'react'

const SHORTCUTS = [
  {
    group: 'Global',
    items: [
      { keys: ['⌘', 'K'], alt: ['Ctrl', 'K'], description: 'Open command palette — search projects, jump anywhere' },
      { keys: ['?'],                            description: 'Show this keyboard shortcuts help' },
      { keys: ['Esc'],                          description: 'Close any modal or panel' },
    ],
  },
  {
    group: 'Command Palette',
    items: [
      { keys: ['↑', '↓'], description: 'Navigate results' },
      { keys: ['↵'],      description: 'Open selected result' },
      { keys: ['Esc'],    description: 'Close palette' },
    ],
  },
  {
    group: 'Workspace',
    items: [
      { keys: ['⌘', 'V'], alt: ['Ctrl', 'V'], description: 'Paste image from clipboard into chat' },
      { keys: ['↵'],                            description: 'Send message (Shift+Enter for new line)' },
      { keys: ['Shift', '↵'],                   description: 'New line in message input' },
    ],
  },
  {
    group: 'Canvas Editor',
    items: [
      { keys: ['⌘', 'Z'], alt: ['Ctrl', 'Z'], description: 'Undo' },
      { keys: ['⌘', 'Shift', 'Z'], alt: ['Ctrl', 'Shift', 'Z'], description: 'Redo' },
      { keys: ['Del'],                          description: 'Delete selected object' },
      { keys: ['⌘', 'D'], alt: ['Ctrl', 'D'], description: 'Duplicate selected object' },
    ],
  },
]

export default function KeyboardShortcutsModal() {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const onKey = (e) => {
      // Don't trigger while typing in inputs
      const tag = (e.target?.tagName || '').toLowerCase()
      const isTyping = tag === 'input' || tag === 'textarea' || e.target?.isContentEditable
      if (e.key === '?' && !isTyping) {
        e.preventDefault()
        setOpen(o => !o)
      } else if (e.key === 'Escape' && open) {
        setOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  if (!open) return null

  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform)

  return (
    <div
      className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => setOpen(false)}
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-xl w-full max-h-[85vh] overflow-hidden border border-slate-200 dark:border-slate-700 flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">Keyboard Shortcuts</h2>
            <p className="text-xs text-slate-500">Press <kbd className="font-mono text-[10px] bg-slate-100 dark:bg-slate-700 px-1 rounded border border-slate-300 dark:border-slate-600">?</kbd> anytime to open this</p>
          </div>
          <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 text-xl w-8 h-8 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center justify-center">✕</button>
        </div>

        {/* Shortcuts list */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {SHORTCUTS.map(group => (
            <div key={group.group}>
              <div className="text-[10px] font-bold uppercase tracking-wider text-zenna-violet mb-2">
                {group.group}
              </div>
              <div className="space-y-1.5">
                {group.items.map((item, i) => {
                  const keys = isMac ? item.keys : (item.alt || item.keys)
                  return (
                    <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-slate-50 dark:hover:bg-slate-700/50">
                      <span className="text-sm text-slate-700 dark:text-slate-300">{item.description}</span>
                      <div className="flex items-center gap-1">
                        {keys.map((k, j) => (
                          <kbd
                            key={j}
                            className="font-mono text-[11px] text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded border border-slate-300 dark:border-slate-600 min-w-[24px] text-center"
                          >
                            {k}
                          </kbd>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 text-[11px] text-slate-500 text-center">
          More shortcuts coming soon — tell us what you want!
        </div>
      </div>
    </div>
  )
}
